/**
 * The money that came off, went back, or never happened.
 *
 * Discounts, refunds, voids and cancellations on one screen, each with its
 * reason and the person — because that is the whole reason an owner opens this:
 * not "how much", which the sales report already says, but "why, and who". The
 * totals are the same figures the sales report nets off, so the two agree.
 */
import { useQuery } from '@tanstack/react-query';
import { formatPKR } from '@foodrun/shared';

import { api, withQuery } from '../../lib/api.js';
import { HBars } from './Charts.jsx';
import { ReportTabs } from './ReportTabs.jsx';
import { usePeriod, PeriodBar, prettyDay } from './period.jsx';
import { Spinner } from '../../components/ui/Spinner.jsx';

const CHANNEL = {
  dine_in: 'Dining',
  takeaway: 'Takeaway',
  delivery: 'Delivery',
  foodpanda: 'Foodpanda',
  phone_order: 'Phone',
  online: 'Online',
};

export default function AdjustmentsPage() {
  const period = usePeriod('day');
  const { data, isLoading, isError } = useQuery({
    queryKey: ['reports', 'adjustments', period.from, period.to],
    queryFn: () => api.get(withQuery('/reports/adjustments', { from: period.from, to: period.to })),
    placeholderData: (previous) => previous,
  });

  return (
    <div className="mx-auto max-w-5xl p-4">
      <Header period={period} />

      {isLoading && !data ? (
        <div className="flex h-[40vh] items-center justify-center">
          <Spinner label="Loading" />
        </div>
      ) : isError || !data ? (
        <p className="py-20 text-center text-sm text-ink-2">Could not load this report.</p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Figure label="Discounts given" value={formatPKR(data.discounts.total)} sub={`${data.discounts.count} times`} tone={data.discounts.total > 0} />
            <Figure label="Refunded" value={formatPKR(data.refunds.total)} sub={`${data.refunds.count} refunds`} tone={data.refunds.total > 0} />
            <Figure label="Voided" value={formatPKR(data.voids.value)} sub={`${data.voids.count} orders`} tone={data.voids.value > 0} />
            <Figure label="Cancelled" value={formatPKR(data.cancelled.value)} sub={`${data.cancelled.count} orders`} tone={data.cancelled.value > 0} />
          </div>

          {/* Discounts — the one worth a chart, because "which reason" is the
              question and a few reasons usually carry most of the money. */}
          <Panel title="Discounts, by reason" className="mt-4">
            {data.discounts.byReason.length === 0 ? (
              <Empty note="No discounts given." />
            ) : (
              <HBars
                data={data.discounts.byReason}
                labelOf={(r) => r.label}
                valueOf={(r) => Number(r.amount) || 0}
                subOf={(r) => `${r.count}×`}
                formatValue={formatPKR}
                color="#f5b544"
              />
            )}
          </Panel>

          <Panel title="Every discount" className="mt-3">
            <ListTable
              rows={data.discounts.list}
              empty="No discounts given."
              cols={[
                ['Order', (r) => r.order_number],
                ['When', (r) => prettyDay(r.date)],
                ['Where', (r) => CHANNEL[r.channel] ?? r.channel],
                ['Reason', (r) => r.reason || r.coupon || '—'],
                ['Given by', (r) => r.approved_by || '—'],
                ['Amount', (r) => formatPKR(Number(r.amount) || 0), 'right'],
              ]}
            />
          </Panel>

          <Panel title="Refunds" className="mt-3">
            <ListTable
              rows={data.refunds.list}
              empty="No refunds."
              cols={[
                ['Order', (r) => r.order_number],
                ['When', (r) => prettyDay(r.date)],
                ['Where', (r) => CHANNEL[r.channel] ?? r.channel],
                ['Reason', (r) => r.reason || '—'],
                ['Back as', (r) => r.method || '—'],
                ['By', (r) => r.by_name || '—'],
                ['Amount', (r) => formatPKR(Number(r.amount) || 0), 'right'],
              ]}
            />
          </Panel>

          <div className="mt-3 grid gap-3 lg:grid-cols-2">
            <Panel title="Voided orders">
              <ListTable
                rows={data.voids.list}
                empty="Nothing voided."
                cols={[
                  ['Order', (r) => r.order_number],
                  ['When', (r) => prettyDay(r.date)],
                  ['Reason', (r) => r.reason || '—'],
                  ['By', (r) => r.by_name || '—'],
                  ['Amount', (r) => formatPKR(Number(r.amount) || 0), 'right'],
                ]}
              />
            </Panel>

            <Panel title="Cancelled orders">
              <ListTable
                rows={data.cancelled.list}
                empty="Nothing cancelled."
                cols={[
                  ['Order', (r) => r.order_number],
                  ['When', (r) => prettyDay(r.date)],
                  ['Where', (r) => CHANNEL[r.channel] ?? r.channel],
                  ['Reason', (r) => r.reason || '—'],
                  ['Amount', (r) => formatPKR(Number(r.amount) || 0), 'right'],
                ]}
              />
            </Panel>
          </div>
        </>
      )}
    </div>
  );
}

function ListTable({ rows, cols, empty }) {
  if (!rows || rows.length === 0) return <Empty note={empty} />;
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[32rem] text-sm">
        <thead>
          <tr className="border-b border-rule text-left text-xs text-ink-3">
            {cols.map(([label, , align]) => (
              <th key={label} className={`px-3 py-2 font-medium ${align === 'right' ? 'text-right' : ''}`}>
                {label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-rule/60 last:border-0">
              {cols.map(([label, get, align], j) => (
                <td
                  key={label}
                  className={`px-3 py-2 ${align === 'right' ? 'text-right tabular-nums font-medium text-ink' : 'text-ink-2'} ${
                    j === 0 ? 'text-ink' : ''
                  }`}
                >
                  {get(r)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Empty({ note }) {
  return <p className="py-6 text-center text-sm text-ink-3">{note}</p>;
}

function Figure({ label, value, sub, tone }) {
  return (
    <div className="rounded-xl border border-rule bg-surface p-3.5">
      <p className="text-xs text-ink-3">{label}</p>
      <p className={`mt-1 font-semibold text-xl tabular-nums ${tone ? 'text-warn' : 'text-ink'}`}>{value}</p>
      {sub && <p className="mt-0.5 text-xs text-ink-3">{sub}</p>}
    </div>
  );
}

function Panel({ title, children, className = '' }) {
  return (
    <section className={`rounded-xl border border-rule bg-surface p-3.5 ${className}`}>
      <h2 className="mb-3 text-sm font-medium text-ink-2">{title}</h2>
      {children}
    </section>
  );
}

function Header({ period }) {
  return (
    <div className="mb-4">
      <ReportTabs />
      <PeriodBar {...period} />
    </div>
  );
}
